import { Location } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild, ViewChildren } from '@angular/core';
import { MatLegacyCheckboxChange as MatCheckboxChange } from '@angular/material/legacy-checkbox';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthenticationService } from '@app/core/authentication/authentication.service';
import { UsersFacade } from '@app/shared/+state/users';
import { FilterComponent } from '@app/shared/components/filter/filter.component';
import { NotificationType, PaginationMeta, Property, User } from '@app/shared/interfaces';
import { Filter } from '@app/shared/models/filter';
import { FilterService } from '@app/shared/services/filter/filter.service';
import { SegmentService } from '@app/shared/services/filter/segment.service';
import { PropertiesService, PropertyIncludes } from '@app/shared/services/properties/properties.service';
import { SegmentEvent, SegmentIoService } from '@app/shared/services/segmentIo/segment-io.service';
import { TitleService } from '@app/shared/services/title/title.service';
import { ToastNotificationsService } from '@app/shared/services/toast-notifications/toast-notifications.service';
import { ReplaySubject } from 'rxjs';
import { map, filter as rxjsFilter, take, takeUntil, tap } from 'rxjs/operators';
import { DialogParentChildPropertySetupComponent } from '../../components/dialog-parent-child-property-setup/dialog-parent-child-property-setup.component';

enum QuickFilters {
  PropertiesTags = 'properties-tags',
}

@Component({
  standalone: false,
  selector: 'sbnb-properties-page',
  templateUrl: './properties.component.html',
  styleUrls: ['./properties.component.scss'],
})
export class PropertiesComponent implements OnInit, OnDestroy {
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  @ViewChild('filterComponent', { static: false }) filterComponent: FilterComponent;
  properties: any[];
  selectedProperties: Property[];
  paginationData: PaginationMeta;
  currentOffset: number;
  activeSegment: string;
  activeSegmentTitle: string;
  activeSegmentUuid: string;
  loading: boolean;
  searchCriteria: string;
  activeFilters: Filter[] = [];
  segmentFilterDefaults: Filter[];
  activeSegmentIdentifier: string;
  newPropertiesHelpUrl =
    'https://my.hospitable.com/support-documentation/article/4639685-how-to-onboard-new-property-to-guest-experience-and-operations';

  quickFilters = [QuickFilters.PropertiesTags];

  @ViewChildren('propertyCheckbox') propertyCheckboxes: any;
  mergingProperties: boolean;

  user: User = this.authService.getUserDetails();

  canUseParentChild$ = this.usersFacade.parentChildEntitlement$.pipe(
    map((entitlement) => {
      return entitlement?.available;
    })
  );

  private hasPropertyLimit$ = this.usersFacade.propertiesEntitlement$.pipe(
    rxjsFilter((entitlement) => Boolean(entitlement)),
    map(({ available, unlimited }) => {
      return available && !unlimited;
    }),
    takeUntil(this.destroyed$),
    tap((hasLimit) => this.setAllPropertiesFilter(hasLimit))
  );

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private propertiesService: PropertiesService,
    private location: Location,
    private titleService: TitleService,
    private segmentService: SegmentService,
    private filter: FilterService,
    private toast: ToastNotificationsService,
    private segmentIoService: SegmentIoService,
    private dialog: MatDialog,
    private authService: AuthenticationService,
    private usersFacade: UsersFacade
  ) {}

  ngOnInit() {
    this.titleService.manuallySet('Properties');
    this.loading = true;
    this.currentOffset = +this.route.snapshot.paramMap.get('offset') || 0;
    this.selectedProperties = [];

    // Check the URL for segment info, and fetch the appropriate filters
    this.activeSegment = this.route.snapshot.paramMap.get('activeSegment');
    this.searchCriteria = this.route.snapshot.paramMap.get('query');

    if (this.activeSegment) {
      this.segmentService.getSegmentById(this.activeSegment, 'properties').subscribe((res) => {
        this.activeFilters = res.filters;
        this.activeSegmentTitle = res.title;
        this.activeSegmentUuid = res.uuid;
        this.activeSegmentIdentifier = res.identifier;
        this.segmentFilterDefaults = this.activeFilters;

        this.titleService.manuallySet(`Properties: ${this.activeSegmentTitle}`);

        // If we stashed filters for this key, retrieve and set them
        const stashedFilters = this.filter.retrieveStashedValuesForKey('properties');
        if (stashedFilters) {
          this.activeFilters = stashedFilters;
          this.segmentFilterDefaults = this.activeFilters;
        }

        this.hasPropertyLimit$.subscribe();

        this.loadProperties(this.activeFilters, this.currentOffset, this.searchCriteria);
      });
    } else {
      this.loadProperties([], this.currentOffset);
    }
  }

  public ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  quickFilterChange(newFilter: any) {
    this.activeFilters = this.activeFilters.filter((x) => x.id !== newFilter.id);
    this.activeFilters = [...this.activeFilters, newFilter];
    this.segmentFilterDefaults = this.activeFilters;
    this.loadProperties(this.activeFilters, 0, this.searchCriteria);

    this.segmentIoService.track(SegmentEvent.PropertiesAppliedFilter, {
      id: newFilter.id,
      placement: 'left',
    });
  }

  private loadProperties(filters: Filter[], offset?: number, search?: string) {
    this.loading = true;

    this.propertiesService
      .getProperties({
        filterCriteria: filters,
        offset,
        search,
        paginate: true,
        includes: [PropertyIncludes.Children],
      })
      .subscribe((res) => {
        this.properties = res.data;
        this.paginationData = res.meta?.pagination;
        this.loading = false;
      });
  }

  openPropertyDetail(propertyId: any) {
    this.router.navigate(['/properties/property', propertyId]);
  }

  public checkboxChecked(event: MatCheckboxChange, property: Property) {
    if (event.checked === true) {
      this.selectedProperties = [...this.selectedProperties, property];
    } else {
      const index = this.selectedProperties.findIndex((prop) => property.id === prop.id);

      if (index !== -1) {
        this.selectedProperties = this.selectedProperties.filter((prop) => prop.id !== property.id);
      }
    }
  }

  startParentChildSetup() {
    this.segmentIoService.track(SegmentEvent.ParentChildConfigStarted, {});

    // Validate against the API first to make sure the properties (should) be able to be merged
    this.propertiesService.validateParentChildProperties([...this.selectedProperties].map((prop) => prop.id)).subscribe(
      (res) => {
        const dialogRef = this.dialog.open(DialogParentChildPropertySetupComponent, {
          maxWidth: '100vw',
          maxHeight: '100vh',
          height: '100%',
          width: '100%',
          disableClose: true,
          autoFocus: false,
          data: {
            properties: [...this.selectedProperties],
          },
        });

        dialogRef.afterClosed().subscribe((result) => {
          this.ngOnInit();
        });
      },
      (err) => {
        this.segmentIoService.track(SegmentEvent.ParentChildConfigValidationFailed, {});
        return;
      }
    );
  }

  public async mergeProperties() {
    this.mergingProperties = true;

    const leadListing = await this.selectLeadListing(this.selectedProperties);

    this.propertiesService
      .mergeProperties(
        this.selectedProperties.map((prop) => prop.id),
        leadListing
      )
      .subscribe(
        (res) => {
          this.mergingProperties = false;
          if (res.error && res.error.errors) {
            const errors = [];
            for (const [key, value] of Object.entries(res.error.errors)) {
              errors.push(value);
            }
            const errorMessage = errors.join(',');
            this.toast.open(errorMessage, 'Dismiss', NotificationType.Error);
            this.deselectAllProperties();
            return;
          }

          this.deselectAllProperties();
          this.loadProperties(this.activeFilters, this.currentOffset, this.searchCriteria);
        },
        (err) => {
          this.mergingProperties = false;
        }
      );
  }

  public deselectAllProperties() {
    this.propertyCheckboxes.forEach((checkbox) => {
      checkbox.checked = false;
    });
    this.selectedProperties = [];
  }

  public handlePageChanged(newPageNo: number) {
    this.currentOffset = newPageNo;

    // We use offsets as the API deals in offsets rather than pages, when requesting data
    const newOffset = newPageNo * this.paginationData.per_page - this.paginationData.per_page;

    // Commit the new offset to the URL - our source of truth for offsets
    const url = this.router.createUrlTree([{ offset: newOffset }], { relativeTo: this.route }).toString();

    this.location.go(url);

    // Reload Conversation Threads based on new offset data, with existing filters
    this.loadProperties(this.activeFilters, newOffset, this.searchCriteria);
  }

  public handleFilterChanged(filters: Filter[]) {
    this.activeFilters = filters;

    if (this.isAllOrNewPropertiesSegment()) {
      const propertiesMutedFilter = filters?.find((filter) => filter.id === 'properties-muted');
      localStorage.setItem('propertiesFilterValue', JSON.stringify(propertiesMutedFilter?.selectedValue ?? ''));
    }

    // Stash the filter values - to persist through page reloads
    this.filter.stashValuesForKey('properties', this.activeFilters);

    // Retrieve conversations based on the new filters
    this.loadProperties(this.activeFilters, 0, this.searchCriteria);

    // Zero out the offset from the URL - we have fresh data so need to go back to offset 0
    const url = this.router
      .createUrlTree([this.searchCriteria ? { query: this.searchCriteria } : {}], { relativeTo: this.route })
      .toString();

    filters.forEach((filter) => {
      if (filter && filter.id) {
        this.segmentIoService.track(SegmentEvent.PropertiesAppliedFilter, {
          id: filter.id,
          placement: 'top',
        });
      }
    });
    this.location.go(url);
  }

  public searchUpdated(searchVal: string) {
    // This is already debounced by the child presentation component
    const url = this.router.createUrlTree([{ query: searchVal }], { relativeTo: this.route }).toString();

    this.location.go(url);

    this.searchCriteria = searchVal;

    this.loadProperties(this.activeFilters, 0, this.searchCriteria);
  }

  filterReset() {
    this.activeFilters = this.segmentFilterDefaults;
    this.deselectAllProperties();
    this.filterComponent.selfReset();
  }

  noItemsButtonClicked(buttonId: string) {
    switch (buttonId) {
      case 'moreinfo':
        window.open(this.newPropertiesHelpUrl, '_blank');
        break;

      default:
        break;
    }
  }

  // if the user has property entitlements that are not unlimited
  // and one of the selected properties is unmuted, while others are muted,
  // pick the first unmuted property as the lead listing to tell the server
  // to make this listing the lead listing so that the merged property will
  // be unmuted.
  private async selectLeadListing(selectedProperties: Property[]): Promise<number> {
    return this.hasPropertyLimit$
      .pipe(
        take(1),
        map((hasLimit) => {
          if (!hasLimit) return null;

          const hasMutedAndUnmuted =
            selectedProperties.some((property) => property.muted) &&
            selectedProperties.some((property) => !property.muted);

          if (!hasMutedAndUnmuted) return null;

          return selectedProperties.find((property) => !property.muted)?.id;
        })
      )
      .toPromise();
  }

  private isAllOrNewPropertiesSegment() {
    return (
      this.activeSegmentIdentifier === 'global-properties-all-types' ||
      this.activeSegmentIdentifier === 'global-properties-new'
    );
  }

  private setAllPropertiesFilter(hasLimit: boolean) {
    if (!this.activeSegmentIdentifier) {
      return;
    }

    const mutedFilterExists = this.activeFilters.find((filter) => filter.id === 'properties-muted');

    if (!hasLimit || !this.isAllOrNewPropertiesSegment() || mutedFilterExists) {
      return;
    }

    const savedFilterValue = JSON.parse(localStorage.getItem('propertiesFilterValue'));
    const mutedFilter = this.filter.mutedAndUnmutedFilter(savedFilterValue);
    if (!mutedFilter) return;

    this.activeFilters.push(mutedFilter);
  }
}
